import { useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@components/ui/Button";
import { FormStatusDialog } from "@components/ui/FormStatusDialog";
import { products } from "@data/products";
import { branches, company } from "@data/company";
import { trackEvent } from "@lib/firebase";
import { AutomotorFields } from "./AutomotorFields";
import styles from "./Contact.module.css";

type Status = "idle" | "submitting" | "success" | "error";

type Errors = Partial<Record<"name" | "phone" | "email" | "interest", string>>;

const MESSAGE_MAX = 500;

const validate = (values: Record<string, string>): Errors => {
  const errors: Errors = {};
  if (!values.name?.trim()) errors.name = "Ingresá tu nombre y apellido.";
  if (!values.phone?.trim()) {
    errors.phone = "Necesitamos un teléfono de contacto.";
  } else if (!/^[+\d\s()-]{8,}$/.test(values.phone)) {
    errors.phone = "Verificá el formato (mínimo 8 dígitos).";
  }
  if (!values.email?.trim()) {
    errors.email = "Ingresá tu email.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
    errors.email = "El email no parece válido.";
  }
  if (!values.interest) errors.interest = "Elegí qué te interesa.";
  return errors;
};

const IconWarn = () => (
  <svg viewBox="0 0 14 14" width={12} height={12} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="7" cy="7" r="6" />
    <path d="M7 4v3M7 10h.01" />
  </svg>
);

const IconPin = () => (
  <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

type ContactFormProps = {
  /** Oculta el header interno (título + subtítulo + promesa). Útil cuando el form se monta dentro de una sección que ya provee ese contexto, como QuickQuote en el Home. */
  compact?: boolean;
};

export function ContactForm({ compact = false }: ContactFormProps = {}) {
  const [searchParams] = useSearchParams();
  const presetProduct = searchParams.get("product");
  const validProductSlugs = new Set([
    ...products.map((p) => p.slug),
    "otro",
  ]);
  const initialInterest =
    presetProduct && validProductSlugs.has(presetProduct) ? presetProduct : "";

  const [status, setStatus] = useState<Status>("idle");
  const [errors, setErrors] = useState<Errors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [messageLen, setMessageLen] = useState(0);
  const [interest, setInterest] = useState<string>(initialInterest);
  const [branch, setBranch] = useState<string>("sin-preferencia");
  const [dialogOpen, setDialogOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const showError = (field: keyof Errors) =>
    touched[field] && errors[field] ? errors[field] : undefined;

  function handleBlur(e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const name = e.target.name;
    setTouched((t) => ({ ...t, [name]: true }));
    if (!formRef.current) return;
    const data = Object.fromEntries(new FormData(formRef.current).entries()) as Record<string, string>;
    setErrors(validate(data));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    // Para multi-checkbox usar getAll; resto getValues
    const data = Object.fromEntries(formData.entries()) as Record<string, string>;
    const coverages = formData.getAll("car_coverage").map(String);
    const validation = validate(data);
    setErrors(validation);
    setTouched({ name: true, phone: true, email: true, interest: true });

    if (Object.keys(validation).length > 0) {
      setStatus("idle");
      const firstError = form.querySelector<HTMLElement>(
        `[name="${Object.keys(validation)[0]}"]`,
      );
      firstError?.focus();
      return;
    }

    setStatus("submitting");
    try {
      const payload = { ...data, car_coverages: coverages };
      await trackEvent("contact_form_submit", {
        interest: data.interest,
        ...(data.interest === "automotor" && {
          car_brand: data.car_brand,
          car_year: data.car_year,
          car_gnc: data.car_gnc,
          coverages_count: coverages.length,
        }),
      });

      const endpoint = import.meta.env.VITE_SEND_QUOTE_URL;
      if (!endpoint) {
        throw new Error("Endpoint no configurado (VITE_SEND_QUOTE_URL)");
      }
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error?.error || `HTTP ${response.status}`);
      }

      setStatus("success");
      setDialogOpen(true);
      formRef.current?.reset();
      setTouched({});
      setMessageLen(0);
      setInterest("");
      setBranch("sin-preferencia");
    } catch (err) {
      console.error("[contact]", err);
      setStatus("error");
      setDialogOpen(true);
    }
  }

  function closeDialog() {
    setDialogOpen(false);
    // status vuelve a idle al cerrar el modal de éxito para permitir un nuevo envío limpio
    if (status === "success") setStatus("idle");
  }

  const interestOptions = [
    ...products.map((p) => ({ value: p.slug, label: p.name })),
    { value: "otro", label: "Otro / Consulta general" },
  ];

  const isAutomotor = interest === "automotor";

  return (
    <form ref={formRef} className={styles.form} onSubmit={handleSubmit} noValidate>
      {!compact && (
        <div className={styles.formHeader}>
          <h2 className={styles.formTitle}>Pedinos una cotización</h2>
          <p className={styles.formSubtitle}>
            Completá los datos y te contactamos con la mejor opción.
          </p>
          <span className={styles.formPromise}>Respuesta en menos de 24 hs hábiles</span>
        </div>
      )}

      <div className={styles.row2}>
        <div className={`${styles.field} ${showError("name") ? styles.fieldError : ""}`}>
          <label htmlFor="name">
            Nombre y apellido <span className={styles.required} aria-hidden>*</span>
          </label>
          <input
            id="name"
            name="name"
            required
            placeholder="María González"
            autoComplete="name"
            onBlur={handleBlur}
            aria-invalid={!!showError("name")}
            aria-describedby={showError("name") ? "err-name" : undefined}
          />
          {showError("name") && (
            <span id="err-name" className={styles.errorMsg}>
              <IconWarn /> {showError("name")}
            </span>
          )}
        </div>
        <div className={`${styles.field} ${showError("phone") ? styles.fieldError : ""}`}>
          <label htmlFor="phone">
            Teléfono <span className={styles.required} aria-hidden>*</span>
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            required
            placeholder="11 6000 0000"
            autoComplete="tel"
            onBlur={handleBlur}
            aria-invalid={!!showError("phone")}
            aria-describedby={showError("phone") ? "err-phone" : undefined}
          />
          {showError("phone") && (
            <span id="err-phone" className={styles.errorMsg}>
              <IconWarn /> {showError("phone")}
            </span>
          )}
        </div>
      </div>

      <div className={`${styles.field} ${showError("email") ? styles.fieldError : ""}`}>
        <label htmlFor="email">
          Email <span className={styles.required} aria-hidden>*</span>
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          placeholder="email@ejemplo.com"
          autoComplete="email"
          onBlur={handleBlur}
          aria-invalid={!!showError("email")}
          aria-describedby={showError("email") ? "err-email" : undefined}
        />
        {showError("email") && (
          <span id="err-email" className={styles.errorMsg}>
            <IconWarn /> {showError("email")}
          </span>
        )}
      </div>

      <div className={`${styles.field} ${showError("interest") ? styles.fieldError : ""}`}>
        <label htmlFor="interest">
          ¿Qué te interesa? <span className={styles.required} aria-hidden>*</span>
        </label>
        <select
          id="interest"
          name="interest"
          required
          value={interest}
          onChange={(e) => setInterest(e.target.value)}
          onBlur={handleBlur}
          aria-invalid={!!showError("interest")}
          aria-describedby={showError("interest") ? "err-interest" : undefined}
        >
          <option value="" disabled>
            Elegí una opción
          </option>
          {interestOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {showError("interest") && (
          <span id="err-interest" className={styles.errorMsg}>
            <IconWarn /> {showError("interest")}
          </span>
        )}
      </div>

      {/* ──────── Campos condicionales — Automotor ──────── */}
      {isAutomotor && <AutomotorFields />}

      {/* ──────── Sucursal — universal ──────── */}
      <div className={styles.field}>
        <label className={styles.toggleLabel}>
          Sucursal de tu preferencia
          <span className={styles.fieldHint}>Opcional</span>
        </label>
        <div className={styles.branchPicker} role="radiogroup" aria-label="Sucursal preferida">
          {branches.map((b) => {
            const value = b.name.toLowerCase().replace(/\s+/g, "-");
            const active = branch === value;
            return (
              <label
                key={b.name}
                className={`${styles.branchOption} ${active ? styles.branchOptionActive : ""}`}
              >
                <input
                  type="radio"
                  name="branch"
                  value={value}
                  checked={active}
                  onChange={() => setBranch(value)}
                />
                <span className={styles.branchOptionIcon}>
                  <IconPin />
                </span>
                <span className={styles.branchOptionInfo}>
                  <span className={styles.branchOptionName}>{b.name}</span>
                  <span className={styles.branchOptionAddr}>
                    {b.city}
                  </span>
                </span>
              </label>
            );
          })}
          <label
            className={`${styles.branchOption} ${branch === "sin-preferencia" ? styles.branchOptionActive : ""}`}
          >
            <input
              type="radio"
              name="branch"
              value="sin-preferencia"
              checked={branch === "sin-preferencia"}
              onChange={() => setBranch("sin-preferencia")}
            />
            <span className={styles.branchOptionIcon}>•</span>
            <span className={styles.branchOptionInfo}>
              <span className={styles.branchOptionName}>Sin preferencia</span>
              <span className={styles.branchOptionAddr}>Nos contactamos por el canal que prefieras</span>
            </span>
          </label>
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="message">Mensaje</label>
        <textarea
          id="message"
          name="message"
          maxLength={MESSAGE_MAX}
          placeholder={
            isAutomotor
              ? "Patente, póliza actual, kilometraje, dudas adicionales..."
              : "Contanos qué necesitás cotizar, datos del bien, vigencia actual..."
          }
          onChange={(e) => setMessageLen(e.target.value.length)}
        />
        <span className={styles.charCount}>
          {messageLen}/{MESSAGE_MAX}
        </span>
      </div>

      <Button
        type="submit"
        variant="cta"
        size="lg"
        disabled={status === "submitting"}
      >
        {status === "submitting" ? (
          <>
            <span className={styles.spinner} aria-hidden /> Enviando...
          </>
        ) : (
          "Enviar consulta"
        )}
      </Button>

      <p className={styles.notice}>
        Al enviar aceptás nuestra política de privacidad. No compartimos tus datos
        con terceros.
      </p>

      <FormStatusDialog
        open={dialogOpen && status === "success"}
        variant="success"
        title="¡Recibimos tu consulta!"
        message="Te respondemos dentro de las próximas 24 horas hábiles al teléfono o email que indicaste."
        primaryLabel="Listo"
        onClose={closeDialog}
      />
      <FormStatusDialog
        open={dialogOpen && status === "error"}
        variant="error"
        title="No pudimos enviar tu consulta"
        message="Hubo un problema al procesar el envío. Probá de nuevo en unos segundos o escribinos directamente por WhatsApp."
        primaryLabel="Reintentar"
        secondary={{
          label: "Ir a WhatsApp",
          href: `https://wa.me/${company.whatsapp}?text=${encodeURIComponent("Hola, quiero una cotización")}`,
          external: true,
        }}
        onClose={closeDialog}
      />
    </form>
  );
}
